import { ProtectedRoute } from '@/config/ProtectedRoute'
import { CustomNavigationClient } from '@/utilities/NavigationClient'
import { MsalProvider } from '@azure/msal-react'
import 'primeicons/primeicons.css'
import 'primereact/resources/primereact.min.css' // core css
import 'primereact/resources/themes/lara-light-indigo/theme.css' // theme
import { lazy, Suspense } from 'react'
import { Route, BrowserRouter as Router, Routes, useNavigate, useParams } from 'react-router'

const Layout = lazy(() => import('@/screens/Layout'))
const Quote = lazy(() => import('@/screens/Quote'))
const CreateQuote = lazy(() => import('@/screens/CreateQuote'))
const Dashboard = lazy(() => import('@/screens/Dashboard'))
const Page404 = lazy(() => import('@/screens/Page404'))
const SearchResult = lazy(() => import('@/screens/SearchResult'))
const User = lazy(() => import('@/screens/User'))
const Branch = lazy(() => import('@/screens/Branch'))
const Profile = lazy(() => import('@/screens/Profile'))
const Onboard = lazy(() => import('@/screens/Onboard'))
const createUser = lazy(() => import('@/screens/createUser'))
const createBranch = lazy(() => import('@/screens/createBranch'))
const DBIndex = lazy(() => import('@/components/db_index/DBIndex'))
const MessageList = lazy(() => import('@/screens/messageList'))
const Customer = lazy(() => import('@/screens/Customer'))
const CustomerUpdate = lazy(() => import('@/screens/CustomerUpdate'))
const NewLogin = lazy(() => import('@/components/NewLogin/NewLogin'))
const UserOnBoarding = lazy(() => import('@/screens/userOnBoarding'))
const DetailScheduling = lazy(() => import('@/components/DetailScheduling/DetailScheduling'))
const DetailSchedulingTableSplitter = lazy(() => import('@/components/DetailSchedulingTableSplitter/DetailSchedulingTableSplitter'))

function App({ msalInstance }) {
  return (
    <div className='App'>
      <Router>
        <Pages msalInstance={msalInstance} />
      </Router>
    </div>
  )
}

function Pages({ msalInstance }) {
  const navigate = useNavigate()
  const navigationClient = new CustomNavigationClient(navigate)
  msalInstance.setNavigationClient(navigationClient)

  return (
    <MsalProvider instance={msalInstance}>
      <Routes>
        <Route
          path='/login'
          element={
            <Suspense fallback={<></>}>
              <NewLogin />
            </Suspense>
          }
        />
        <Route
          path='/useronboarding'
          element={
            <Suspense fallback={<></>}>
              <UserOnBoarding />
            </Suspense>
          }
        />
        <Route
          path='/admin-login'
          element={
            <Suspense fallback={<></>}>
              <NewLogin />
            </Suspense>
          }
        />

        <Route
          path='/'
          element={
            <Suspense fallback={<></>}>
              <Layout />
            </Suspense>
          }
          exact
        >
          <Route
            path=''
            element={
              <Suspense fallback={<></>}>
                <Dashboard />
              </Suspense>
            }
          />
          <Route
            path='product_search'
            element={
              <Suspense fallback={<></>}>
                <DBIndex />
              </Suspense>
            }
          />
          <Route
            path='searchresult/:id'
            element={
              <Suspense fallback={<></>}>
                <SearchResult />
              </Suspense>
            }
          />
          <Route
            path='quote/:id'
            element={
              <Suspense fallback={<></>}>
                <QuoteRoute />
              </Suspense>
            }
          />
          <Route
            path='quote/:id/split'
            element={
              <Suspense fallback={<></>}>
                <DetailSchedulingTableSplitter />
              </Suspense>
            }
          />
          <Route
            path='quote/:id/shipments'
            element={
              <Suspense fallback={<></>}>
                <DetailScheduling />
              </Suspense>
            }
          />
          <Route
            path='message/:id'
            element={
              <Suspense fallback={<></>}>
                <MessageList />
              </Suspense>
            }
          />
          <Route
            path='create-quote'
            element={
              <Suspense fallback={<></>}>
                <CreateQuote />
              </Suspense>
            }
          />
          <Route
            path='/onboard'
            element={
              <Suspense fallback={<></>}>
                <Onboard />
              </Suspense>
            }
          />
          <Route
            path='customer'
            element={
              <Suspense fallback={<></>}>
                <ProtectedRoute component={Customer} scopes={['Scopes.Customer.List', 'Scopes.Tenant.All', 'Scopes.Global.All']} />
              </Suspense>
            }
          />

          <Route
            path='customer/create'
            element={
              <Suspense fallback={<></>}>
                <ProtectedRoute component={CustomerUpdate} scopes={['Scopes.Customer.List', 'Scopes.Tenant.All', 'Scopes.Global.All']} />
              </Suspense>
            }
          />

          <Route
            path='customer/:customer_id'
            element={
              <Suspense fallback={<></>}>
                <ProtectedRoute component={CustomerUpdate} scopes={['Scopes.Customer.View', 'Scopes.Tenant.All', 'Scopes.Global.All']} />
              </Suspense>
            }
          />
          <Route
            path='user'
            element={
              <Suspense fallback={<></>}>
                <ProtectedRoute component={User} scopes={['Scopes.User.List', 'Scopes.Tenant.All', 'Scopes.Global.All']} />
              </Suspense>
            }
          />
          <Route
            path='user/create'
            element={
              <Suspense fallback={<></>}>
                <ProtectedRoute component={createUser} scopes={['Scopes.User.List', 'Scopes.Tenant.All', 'Scopes.Global.All']} />
              </Suspense>
            }
          />
          <Route
            path='user/:id'
            element={
              <Suspense fallback={<></>}>
                <ProtectedRoute component={createUser} scopes={['Scopes.User.View', 'Scopes.Tenant.All', 'Scopes.Global.All']} />
              </Suspense>
            }
          />
          <Route
            path='branch'
            element={
              <Suspense fallback={<></>}>
                <ProtectedRoute component={Branch} scopes={['Scopes.Tenant.All', 'Scopes.Global.All']} />
              </Suspense>
            }
          />
          <Route
            path='branch/create'
            element={
              <Suspense fallback={<></>}>
                <ProtectedRoute component={createBranch} scopes={['Scopes.Tenant.All', 'Scopes.Global.All']} />
              </Suspense>
            }
          />
          <Route
            path='branch/:id'
            element={
              <Suspense fallback={<></>}>
                <ProtectedRoute component={createBranch} scopes={['Scopes.Tenant.All', 'Scopes.Global.All']} />
              </Suspense>
            }
          />

          <Route
            path='profile'
            element={
              <Suspense fallback={<></>}>
                <ProtectedRoute component={Profile} scopes={['Scopes.Tenant.All']} />
              </Suspense>
            }
          />
        </Route>
        <Route
          path='*'
          element={
            <Suspense fallback={<></>}>
              <Page404 />
            </Suspense>
          }
        />
      </Routes>
    </MsalProvider>
  )
}

function QuoteRoute() {
  const { id } = useParams()

  return <Quote key={id} />
}

export default App
